/**
 * Realtime feed for Uber guest trips.
 *
 * "No driver available" and "driver cancelled" are the two states ops must see
 * immediately — a trip in either state is dead until somebody re-books it. This
 * service delivers those (and every other status change) to any mounted Uber
 * screen.
 *
 * Transport: socket.io, with an automatic polling fallback so the screens keep
 * working if the namespace is unreachable. Nothing here throws if the backend
 * is absent.
 *
 * Server contract:
 *   namespace : <api host>/sp/uber   path /socketSp   (same path as Map.vue)
 *   handshake : query { token }  - the server resolves the service partner from
 *               it and joins that partner's room; no client-side subscribe call
 *   listen    : 'uberTripUpdate'  { tripId, uberStatus, attentionCount, ... }
 *   listen    : 'uberTripMessage' { tripId, message }  - a driver reply
 * Polling fallback hits POST /uber/trip/updates with the last sync timestamp.
 */

import io from 'socket.io-client';
import axios from '@/axios/axios-global';
import { isCritical } from '@/config/uberStatus';

const SOCKET_NAMESPACE = '/sp/uber';
const SOCKET_PATH = '/socketSp';

/**
 * Socket host, derived from the API base URL rather than hardcoded - a fixed
 * production host never connects on staging or against a dev backend.
 * baseURL ends in /web; the socket server is mounted at the root.
 */
function socketOrigin() {
  const base = axios.defaults.baseURL || '';
  return base.replace(/\/web\/?$/, '');
}

const POLL_INTERVAL_MS = 20000;
const SOCKET_GRACE_MS = 6000;

class UberRealtime {
  constructor() {
    this.socket = null;
    this.pollTimer = null;
    this.graceTimer = null;
    this.listeners = { update: [], critical: [], connection: [], message: [] };
    this.subscribers = 0;
    this.connected = false;
    this.lastSync = null;
    this.seenCritical = new Set();
  }

  /* ------------------------------------------------------------------ *
   * Subscription
   * ------------------------------------------------------------------ */

  /**
   * Register a component. Returns an unsubscribe function — call it in
   * beforeDestroy so the socket closes when the last screen unmounts.
   */
  subscribe(handlers = {}) {
    Object.keys(handlers).forEach((event) => {
      if (this.listeners[event]) this.listeners[event].push(handlers[event]);
    });

    this.subscribers += 1;
    if (this.subscribers === 1) this.start();

    return () => {
      Object.keys(handlers).forEach((event) => {
        if (!this.listeners[event]) return;
        const index = this.listeners[event].indexOf(handlers[event]);
        if (index !== -1) this.listeners[event].splice(index, 1);
      });
      this.subscribers = Math.max(0, this.subscribers - 1);
      if (this.subscribers === 0) this.stop();
    };
  }

  emit(event, payload) {
    (this.listeners[event] || []).forEach((handler) => {
      try {
        handler(payload);
      } catch (err) {
        // A broken listener must never take down the feed.
        console.error('uberRealtime listener failed', err);
      }
    });
  }

  /* ------------------------------------------------------------------ *
   * Lifecycle
   * ------------------------------------------------------------------ */

  start() {
    this.lastSync = Date.now();
    this.connectSocket();

    // If the socket has not connected within the grace window, assume the
    // namespace is not live yet and poll instead.
    this.graceTimer = setTimeout(() => {
      if (!this.connected) this.startPolling();
    }, SOCKET_GRACE_MS);
  }

  stop() {
    if (this.graceTimer) clearTimeout(this.graceTimer);
    this.graceTimer = null;
    this.stopPolling();

    if (this.socket) {
      try {
        this.socket.close();
      } catch (err) {
        /* already gone */
      }
      this.socket = null;
    }
    this.connected = false;
    this.seenCritical.clear();
  }

  connectSocket() {
    // The server resolves the service partner from this token and joins the
    // matching room itself - the client is not trusted to name its own room,
    // and the id it holds (sp.vendor) is not the one trips are keyed by.
    let token = null;
    try {
      token = localStorage.getItem('vendorLoginToken');
    } catch (err) {
      token = null;
    }

    try {
      this.socket = io.connect(socketOrigin() + SOCKET_NAMESPACE, {
        path: SOCKET_PATH,
        query: { token },
        transports: ['websocket', 'polling'],
        reconnectionAttempts: 5,
        timeout: 5000,
      });
    } catch (err) {
      this.startPolling();
      return;
    }

    this.socket.on('connect', () => {
      this.connected = true;
      this.stopPolling();
      this.emit('connection', { connected: true, transport: 'socket' });
    });

    this.socket.on('uberTripUpdate', (payload) => {
      this.handleUpdate(payload);
    });

    // Driver replies. Fanned out on their own channel: only an open message
    // thread cares, and it must not look like a status change.
    this.socket.on('uberTripMessage', (payload) => {
      if (payload) this.emit('message', payload);
    });

    this.socket.on('disconnect', () => {
      this.connected = false;
      this.emit('connection', { connected: false, transport: 'socket' });
      if (this.subscribers > 0) this.startPolling();
    });

    this.socket.on('connect_error', () => {
      this.connected = false;
      if (this.subscribers > 0) this.startPolling();
    });
  }

  /* ------------------------------------------------------------------ *
   * Polling fallback
   * ------------------------------------------------------------------ */

  startPolling() {
    if (this.pollTimer) return;
    this.emit('connection', { connected: true, transport: 'poll' });
    this.pollTimer = setInterval(() => this.poll(), POLL_INTERVAL_MS);
    this.poll();
  }

  stopPolling() {
    if (this.pollTimer) clearInterval(this.pollTimer);
    this.pollTimer = null;
  }

  async poll() {
    try {
      const response = await axios.post('uber/trip/updates', { since: this.lastSync });
      const updates = (response.data && response.data.updates) || [];
      this.lastSync = (response.data && response.data.now) || Date.now();
      updates.forEach((update) => this.handleUpdate(update));
    } catch (err) {
      // Endpoint not built yet, or a transient failure. Stay quiet and retry on
      // the next tick — the screens keep working off their own list calls.
    }
  }

  /* ------------------------------------------------------------------ *
   * Fan-out
   * ------------------------------------------------------------------ */

  handleUpdate(payload) {
    if (!payload) return;
    const status = payload.uberStatus || (payload.uberDetails && payload.uberDetails.uberStatus);
    const normalised = { ...payload, uberStatus: status };

    this.emit('update', normalised);

    // De-dupe criticals: polling re-sends the same row until it is acted on,
    // and we do not want to alert ops on every tick for the same trip.
    if (isCritical(status)) {
      const key = `${payload.tripId}:${status}`;
      if (!this.seenCritical.has(key)) {
        this.seenCritical.add(key);
        this.emit('critical', normalised);
      }
    }
  }

  /**
   * Called after ops has acted on a trip so a later regression back into the
   * same critical state alerts again.
   */
  clearCritical(tripId) {
    Array.from(this.seenCritical)
      .filter((key) => key.startsWith(`${tripId}:`))
      .forEach((key) => this.seenCritical.delete(key));
  }
}

export default new UberRealtime();
